import { useState } from "react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  haftalikZiyaretci, enCokIncelenenKampanya, enYuksekDonusumKampanya,
} from "@/mocks/dashboard";

const CARD_BG = "#141720";
const BORDER = "#1e2130";
const TEXT_PRIMARY = "#e8eaf0";
const TEXT_SECONDARY = "#5a5f7a";
const TEXT_MUTED = "#3a3d4a";

const metrikKartlar = [
  { baslik: "Toplam Ziyaretçi", deger: "12.847", trend: "+12.4%", trendYon: "up", ikon: "ri-user-line", renk: "from-violet-600 to-indigo-700", accent: "#7c3aed" },
  { baslik: "Toplam Başvuru", deger: "1.423", trend: "+8.7%", trendYon: "up", ikon: "ri-file-list-3-line", renk: "from-emerald-500 to-teal-600", accent: "#10b981" },
  { baslik: "WhatsApp Tıklamaları", deger: "876", trend: "+15.2%", trendYon: "up", ikon: "ri-whatsapp-line", renk: "from-green-500 to-emerald-600", accent: "#22c55e" },
  { baslik: "Ort. Sayfada Kalma", deger: "4:32", trend: "-2.1%", trendYon: "down", ikon: "ri-time-line", renk: "from-amber-500 to-orange-600", accent: "#f59e0b" },
];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ backgroundColor: "#1e2130", border: "1px solid #2a2d3a", borderRadius: 12, padding: "10px 14px" }}>
        <p style={{ fontSize: 11, fontWeight: 600, color: "#8b8fa8", marginBottom: 4 }}>{label}</p>
        {payload.map((p: any, i: number) => (
          <p key={i} style={{ fontSize: 11, color: p.color }}>
            {p.name === "ziyaretci" ? "Ziyaretçi" : "Başvuru"}: <strong>{p.value.toLocaleString("tr-TR")}</strong>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function GenelBakis() {
  const [aktifGrafik, setAktifGrafik] = useState<"ziyaretci" | "basvuru">("ziyaretci");

  return (
    <div className="space-y-6">
      {/* KPI Kartları */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {metrikKartlar.map((kart, i) => (
          <div
            key={i}
            className="rounded-2xl p-5 transition-all duration-200 group cursor-default"
            style={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}` }}
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${kart.renk} flex items-center justify-center`}>
                <i className={`${kart.ikon} text-white text-lg`}></i>
              </div>
              <span
                className="text-xs font-semibold px-2 py-1 rounded-full"
                style={{
                  backgroundColor: kart.trendYon === "up" ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.15)",
                  color: kart.trendYon === "up" ? "#10b981" : "#ef4444",
                }}
              >
                {kart.trendYon === "up" ? "↑" : "↓"} {kart.trend}
              </span>
            </div>
            <p className="text-2xl font-bold mb-1" style={{ color: TEXT_PRIMARY }}>{kart.deger}</p>
            <p className="text-sm" style={{ color: TEXT_SECONDARY }}>{kart.baslik}</p>
          </div>
        ))}
      </div>

      {/* Grafik + Kampanya Kartları */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Grafik */}
        <div
          className="lg:col-span-3 rounded-2xl p-5"
          style={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}` }}
        >
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-sm font-bold" style={{ color: TEXT_PRIMARY }}>Son 7 Günlük Trafik</h3>
              <p className="text-xs mt-0.5" style={{ color: TEXT_SECONDARY }}>Ziyaretçi ve başvuru trendi</p>
            </div>
            <div className="flex items-center gap-1 rounded-lg p-1" style={{ backgroundColor: "#0d0f16" }}>
              {(["ziyaretci", "basvuru"] as const).map((key) => (
                <button
                  key={key}
                  onClick={() => setAktifGrafik(key)}
                  className="px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
                  style={{
                    backgroundColor: aktifGrafik === key ? "#1e2130" : "transparent",
                    color: aktifGrafik === key ? "#a78bfa" : TEXT_SECONDARY,
                  }}
                >
                  {key === "ziyaretci" ? "Ziyaretçi" : "Başvuru"}
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={haftalikZiyaretci} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gradViolet" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#7c3aed" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradGreen" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e2130" />
              <XAxis dataKey="gun" tick={{ fontSize: 11, fill: TEXT_MUTED }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: TEXT_MUTED }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              {aktifGrafik === "ziyaretci" ? (
                <Area type="monotone" dataKey="ziyaretci" stroke="#7c3aed" strokeWidth={2.5} fill="url(#gradViolet)" dot={{ fill: "#7c3aed", r: 4, strokeWidth: 0 }} activeDot={{ r: 6, fill: "#7c3aed" }} />
              ) : (
                <Area type="monotone" dataKey="basvuru" stroke="#10b981" strokeWidth={2.5} fill="url(#gradGreen)" dot={{ fill: "#10b981", r: 4, strokeWidth: 0 }} activeDot={{ r: 6, fill: "#10b981" }} />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Kampanya Kartları */}
        <div className="lg:col-span-2 flex flex-col gap-4">
          <div className="rounded-2xl p-5 flex-1" style={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}` }}>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg" style={{ backgroundColor: "rgba(245,158,11,0.15)" }}>
                <i className="ri-fire-line text-sm" style={{ color: "#f59e0b" }}></i>
              </div>
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: TEXT_SECONDARY }}>En Çok İncelenen</p>
            </div>
            <p className="text-sm font-bold mb-3" style={{ color: TEXT_PRIMARY }}>{enCokIncelenenKampanya.ad}</p>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold" style={{ color: "#f59e0b" }}>{enCokIncelenenKampanya.goruntulenme.toLocaleString("tr-TR")}</p>
                <p className="text-xs" style={{ color: TEXT_SECONDARY }}>görüntülenme</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold" style={{ color: TEXT_PRIMARY }}>%{enCokIncelenenKampanya.donusum}</p>
                <p className="text-xs" style={{ color: TEXT_SECONDARY }}>dönüşüm</p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl p-5 flex-1" style={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}` }}>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg" style={{ backgroundColor: "rgba(16,185,129,0.15)" }}>
                <i className="ri-trophy-line text-sm" style={{ color: "#10b981" }}></i>
              </div>
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: TEXT_SECONDARY }}>En Yüksek Dönüşüm</p>
            </div>
            <p className="text-sm font-bold mb-3" style={{ color: TEXT_PRIMARY }}>{enYuksekDonusumKampanya.ad}</p>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold" style={{ color: "#10b981" }}>%{enYuksekDonusumKampanya.donusum}</p>
                <p className="text-xs" style={{ color: TEXT_SECONDARY }}>dönüşüm oranı</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold" style={{ color: TEXT_PRIMARY }}>{enYuksekDonusumKampanya.goruntulenme.toLocaleString("tr-TR")}</p>
                <p className="text-xs" style={{ color: TEXT_SECONDARY }}>görüntülenme</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hızlı Özet */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: "Bugün Gelen Ziyaretçi", deger: "1.537", ikon: "ri-user-add-line", accent: "#7c3aed", bg: "rgba(124,58,237,0.15)" },
          { label: "Bugün Gelen Başvuru", deger: "212", ikon: "ri-file-add-line", accent: "#10b981", bg: "rgba(16,185,129,0.15)" },
          { label: "Aktif Kampanya", deger: "5", ikon: "ri-megaphone-line", accent: "#f59e0b", bg: "rgba(245,158,11,0.15)" },
        ].map((item, i) => (
          <div
            key={i}
            className="rounded-2xl p-4 flex items-center gap-4"
            style={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}` }}
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: item.bg }}>
              <i className={`${item.ikon} text-lg`} style={{ color: item.accent }}></i>
            </div>
            <div>
              <p className="text-xl font-bold" style={{ color: TEXT_PRIMARY }}>{item.deger}</p>
              <p className="text-xs" style={{ color: TEXT_SECONDARY }}>{item.label}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
